import { API_ROUTES } from '@app/common';
import type {
  PermissionOverride,
  Team,
  TenantContext,
  TenantInvitationSummary,
  TenantMemberSummary,
  TenantMembershipSummary,
  TenantRole,
  TenantStats,
  TenantSubscription,
  TenantSummary,
} from '@app/common';
import { apiFetch } from './index';

export type {
  Permission,
  PermissionOverride,
  Team,
  TenantContext,
  TenantInvitationSummary,
  TenantMemberSummary,
  TenantMembershipSummary,
  TenantRole,
  TenantStats,
  TenantSubscription,
  TenantSummary,
} from '@app/common';

// ─── The caller's own workspaces ─────────────────────────────────────────────

export const listMyTenants = (): Promise<TenantMembershipSummary[]> =>
  apiFetch<TenantMembershipSummary[]>(API_ROUTES.TENANTS);

export const getTenantContext = (tenantId: string): Promise<TenantContext> =>
  apiFetch<TenantContext>(API_ROUTES.TENANT_CONTEXT(tenantId));

// ─── Roster ──────────────────────────────────────────────────────────────────

export const listMembers = (tenantId: string): Promise<TenantMemberSummary[]> =>
  apiFetch<TenantMemberSummary[]>(API_ROUTES.TENANT_MEMBERS(tenantId));

/**
 * Returns a member when the address already has an account and an invitation
 * when it does not — the backend decides, because only it can look the address
 * up. Call sites discriminate on `userId`.
 */
export const inviteMember = (
  tenantId: string,
  input: { email: string; role: TenantRole }
): Promise<TenantInvitationSummary | TenantMemberSummary> =>
  apiFetch<TenantInvitationSummary | TenantMemberSummary>(API_ROUTES.TENANT_MEMBERS(tenantId), {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const removeMember = (tenantId: string, userId: string): Promise<{ removed: true }> =>
  apiFetch<{ removed: true }>(API_ROUTES.TENANT_MEMBER(tenantId, userId), { method: 'DELETE' });

export const changeMemberRole = (
  tenantId: string,
  userId: string,
  role: TenantRole
): Promise<{ role: TenantRole }> =>
  apiFetch<{ role: TenantRole }>(API_ROUTES.TENANT_MEMBER_ROLE(tenantId, userId), {
    method: 'POST',
    body: JSON.stringify({ role }),
  });

/** Replaces the whole override set — an empty array puts the member back on their role defaults. */
export const setMemberPermissions = (
  tenantId: string,
  userId: string,
  overrides: PermissionOverride[]
): Promise<TenantMemberSummary> =>
  apiFetch<TenantMemberSummary>(API_ROUTES.TENANT_MEMBER_PERMISSIONS(tenantId, userId), {
    method: 'POST',
    body: JSON.stringify({ overrides }),
  });

export const setTeamVisibility = (
  tenantId: string,
  userId: string,
  teamIds: string[]
): Promise<{ teamIds: string[] }> =>
  apiFetch<{ teamIds: string[] }>(API_ROUTES.TENANT_MEMBER_TEAM_VISIBILITY(tenantId, userId), {
    method: 'POST',
    body: JSON.stringify({ teamIds }),
  });

// ─── Invitations ─────────────────────────────────────────────────────────────

export const listInvitations = (tenantId: string): Promise<TenantInvitationSummary[]> =>
  apiFetch<TenantInvitationSummary[]>(API_ROUTES.TENANT_INVITATIONS(tenantId));

export const revokeInvitation = (
  tenantId: string,
  invitationId: string
): Promise<{ revoked: true }> =>
  apiFetch<{ revoked: true }>(API_ROUTES.TENANT_INVITATION(tenantId, invitationId), {
    method: 'DELETE',
  });

// ─── Teams ───────────────────────────────────────────────────────────────────

export const listTeams = (tenantId: string): Promise<Team[]> =>
  apiFetch<Team[]>(API_ROUTES.TENANT_TEAMS(tenantId));

export const createTeam = (tenantId: string, name: string): Promise<Team> =>
  apiFetch<Team>(API_ROUTES.TENANT_TEAMS(tenantId), {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const deleteTeam = (tenantId: string, teamId: string): Promise<{ deleted: true }> =>
  apiFetch<{ deleted: true }>(API_ROUTES.TENANT_TEAM(tenantId, teamId), { method: 'DELETE' });

export const setTeamMembers = (
  tenantId: string,
  teamId: string,
  userIds: string[]
): Promise<Team> =>
  apiFetch<Team>(API_ROUTES.TENANT_TEAM_MEMBERS(tenantId, teamId), {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });

// ─── Plan and analytics ──────────────────────────────────────────────────────

export const getTenantSubscription = (tenantId: string): Promise<TenantSubscription> =>
  apiFetch<TenantSubscription>(API_ROUTES.TENANT_SUBSCRIPTION(tenantId));

export const setTenantPlan = (tenantId: string, plan: string): Promise<TenantSubscription> =>
  apiFetch<TenantSubscription>(API_ROUTES.TENANT_SUBSCRIPTION(tenantId), {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });

export const getTenantStats = (tenantId: string, days = 30): Promise<TenantStats> =>
  apiFetch<TenantStats>(`${API_ROUTES.TENANT_STATS(tenantId)}?days=${days}`);

// ─── Superadmin ──────────────────────────────────────────────────────────────

export const adminListTenants = (search?: string): Promise<TenantSummary[]> =>
  apiFetch<TenantSummary[]>(
    search ? `${API_ROUTES.ADMIN_TENANTS}?search=${encodeURIComponent(search)}` : API_ROUTES.ADMIN_TENANTS
  );

export const adminCreateTenant = (input: {
  name: string;
  adminEmail: string;
  plan?: string;
}): Promise<TenantSummary> =>
  apiFetch<TenantSummary>(API_ROUTES.ADMIN_TENANTS, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const adminDeleteTenant = (tenantId: string): Promise<{ deleted: true }> =>
  apiFetch<{ deleted: true }>(API_ROUTES.ADMIN_TENANT(tenantId), { method: 'DELETE' });

export const adminReplaceTenantAdmin = (
  tenantId: string,
  input: { newAdminEmail: string; removeUserId?: string }
): Promise<{ changed: true }> =>
  apiFetch<{ changed: true }>(API_ROUTES.ADMIN_TENANT_ADMIN(tenantId), {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const adminSetTenantPlan = (tenantId: string, plan: string): Promise<TenantSubscription> =>
  apiFetch<TenantSubscription>(API_ROUTES.ADMIN_TENANT_PLAN(tenantId), {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
